import { ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnDestroy, Optional, Output, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { chevronDownIcon, progressIcon } from '../../../icons';

export interface ISelectOptions {
    value: string | number;
    displayName: string;
}

@Component({
    selector: 'app-select',
    template: `
        <label class="selector">
            <select
                class="selector__select"
                [ngClass]="cssClasses"
                [class.selector__select--not-empty]="selectedValue"
                [class.ng-invalid]="invalid"
                [class.ng-untouched]="untouched"
                [disabled]="isDisabled"
                (change)="onSelectChange($any($event.target).value)"
                (blur)="onTouched()"
            >
                <option [value]="" selected="true" disabled="disabled">{{ selectedValue && placeholderOption }}</option>
                <option *ngFor="let option of options" [value]="option.value" [selected]="selectedValue === option.value">
                    {{ option.displayName }}
                </option>
            </select>
            <i *ngIf="!loading" class="selector__icon">${chevronDownIcon}</i>
            <i *ngIf="loading" class="selector__icon selector__progress">${progressIcon}</i>
            <span class="selector__label">{{ label }}</span>

            <p class="help is-danger" *ngIf="errors && !untouched">
                <ng-container *ngIf="errors?.required">
                    {{ 'forms.validation_message____field_required' | translate }}
                </ng-container>
            </p>
        </label>
    `,
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {
    // eslint-disable-next-line @angular-eslint/no-output-native
    @HostBinding('class') class = 'select';
    @Input() label: string;
    @Input() options?: ISelectOptions[] | null;
    @Input() cssClasses: string;
    @Input() selectedValue?: ISelectOptions['value'] | null;
    @Input() placeholderOption?: string;
    @Input()
    get loading() {
        return this._loading;
    }
    set loading(value) {
        this._loading = value;

        if (this._loading) {
            this.wasDisabled = this.isDisabled;
            this.isDisabled = true;
        } else {
            this.isDisabled = this.wasDisabled;
        }
    }

    @Output() selectChange = new EventEmitter<ISelectOptions['value']>();

    isDisabled = false;
    wasDisabled = false;
    setDisabledStateTimeout: any;

    get errors() {
        return this.controlDir?.control?.errors;
    }
    get untouched() {
        return this.controlDir?.control?.untouched;
    }
    get invalid() {
        return this.controlDir?.control?.invalid;
    }

    private _loading = false;

    constructor(@Self() @Optional() private controlDir: NgControl, private cdr: ChangeDetectorRef) {
        if (controlDir) {
            controlDir.valueAccessor = this;
        }
    }

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    onTouched: () => void = () => void 0;

    onChange(value: ISelectOptions['value']): void {
        this.selectChange.emit(value);
    }

    writeValue(value: any): void {
        this.selectedValue = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.setDisabledStateTimeout = setTimeout(() => {
            this.isDisabled = isDisabled;
            this.cdr.markForCheck();
        });
    }

    onSelectChange(value: string) {
        this.selectedValue = value;
        this.onChange(value);
    }

    ngOnDestroy(): void {
        clearTimeout(this.setDisabledStateTimeout);
    }
}
